import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  Tag,
  Coins,
  HandCoins,
  CheckCircle,
  CircleDollarSign,
  X,
} from 'lucide-react';
import DataTable from '../DataTable';
import IconButton from '../common/IconButton';
import ConfirmDialog from '../ConfirmDialog';
import TithePaymentFormModal from './TithePaymentFormModal';
import {
  tithesApi,
  TithePaymentDto,
  TitheSummaryDto,
  formatCurrency,
  formatDate,
} from '../../services/tithesService';

const Container = styled.div`
  padding: 24px 0;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  padding: 14px 16px;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const DateInput = styled.input`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  direction: ltr;
  text-align: right;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--surface-color);
  color: var(--text-secondary);
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: var(--hover-background);
  }
`;

const RangeHint = styled.span`
  font-size: 12px;
  color: var(--text-tertiary);
  margin-inline-start: auto;
  align-self: center;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  margin: 4px 0 12px;
`;

// הבהרת בסיס החישוב - קריטי שהמשתמש יבין שהמעשר מחושב על הכנסה נטו
const SectionNote = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: var(--text-tertiary);
  margin: -6px 0 12px;
  max-width: 640px;
`;

const SummaryGrid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols ?? 3}, 1fr);
  gap: 14px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

// כרטיס מעודן וניטרלי. הדגשה עדינה (מסגרת + ערך בצבע primary) רק לכרטיסי "שורת התחתית".
const StatCard = styled.div<{ $emphasis?: boolean }>`
  background: var(--surface-color);
  border: 1px solid ${props => (props.$emphasis ? 'var(--primary-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-md);
  padding: 16px 18px;
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const StatTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const StatIcon = styled.div`
  color: var(--text-tertiary);
  display: flex;
`;

const StatValue = styled.div<{ $primary?: boolean; $danger?: boolean }>`
  font-size: 23px;
  font-weight: 700;
  color: ${props =>
    props.$primary ? 'var(--primary-color)' : props.$danger ? '#b91c1c' : 'var(--text-primary)'};
  direction: ltr;
  text-align: right;

  @media (max-width: 767px) {
    font-size: 20px;
  }
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  background: var(--primary-color);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: var(--primary-hover);
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 4px;
  justify-content: center;
`;

const NotesCell = styled.span`
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
`;

const TithesTab: React.FC = () => {
  // ברירת מחדל: ריק = כל הנתונים (כל המערכת)
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [summary, setSummary] = useState<TitheSummaryDto | null>(null);
  const [payments, setPayments] = useState<TithePaymentDto[]>([]);
  const [loading, setLoading] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<TithePaymentDto | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<TithePaymentDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const data = await tithesApi.getSummary(dateFrom || undefined, dateTo || undefined);
      setSummary(data);
    } catch {
      toast.error('שגיאה בטעינת סיכום המעשרות');
    }
  }, [dateFrom, dateTo]);

  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await tithesApi.listPayments(dateFrom || undefined, dateTo || undefined);
      setPayments(data);
    } catch {
      toast.error('שגיאה בטעינת תשלומי המעשר');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const refreshAll = () => {
    loadSummary();
    loadPayments();
  };

  const handleClearFilter = () => {
    setDateFrom('');
    setDateTo('');
  };

  const hasFilter = !!dateFrom || !!dateTo;

  const handleAdd = () => {
    setEditingPayment(null);
    setIsFormOpen(true);
  };

  const handleEdit = (payment: TithePaymentDto) => {
    setEditingPayment(payment);
    setIsFormOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await tithesApi.remove(deleteTarget.id);
      toast.success('תשלום המעשר נמחק בהצלחה');
      setDeleteTarget(null);
      refreshAll();
    } catch {
      toast.error('שגיאה במחיקת תשלום המעשר');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'amount',
      title: 'סכום',
      render: (value: number) => formatCurrency(value),
    },
    {
      key: 'paymentDate',
      title: 'תאריך תשלום',
      render: (value: string) => formatDate(value),
    },
    {
      key: 'notes',
      title: 'הערות',
      render: (value: string) => (value ? <NotesCell title={value}>{value}</NotesCell> : '-'),
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '120px',
      render: (_: any, record: TithePaymentDto) => (
        <RowActions>
          <IconButton icon={Edit} title="עריכה" variant="primary" onClick={() => handleEdit(record)} />
          <IconButton icon={Trash2} title="מחיקה" variant="danger" onClick={() => setDeleteTarget(record)} />
        </RowActions>
      ),
    },
  ];

  return (
    <Container>
      {/* סינון לפי טווח תאריכים - ריק = כל המערכת */}
      <FilterBar>
        <FilterField>
          <FilterLabel htmlFor="tithe-from">מתאריך</FilterLabel>
          <DateInput
            id="tithe-from"
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={e => setDateFrom(e.target.value)}
          />
        </FilterField>
        <FilterField>
          <FilterLabel htmlFor="tithe-to">עד תאריך</FilterLabel>
          <DateInput
            id="tithe-to"
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={e => setDateTo(e.target.value)}
          />
        </FilterField>
        {hasFilter && (
          <ClearButton type="button" onClick={handleClearFilter}>
            <X size={14} />
            נקה סינון
          </ClearButton>
        )}
        <RangeHint>{hasFilter ? 'מוצג טווח מסונן' : 'מוצגים נתוני כל המערכת'}</RangeHint>
      </FilterBar>

      {/* שורה ראשונה - סיכום פיננסי */}
      <SectionTitle>סיכום פיננסי</SectionTitle>
      <SectionNote>
        בסיס המעשר מחושב על הכנסה נטו. תשלומי הלקוחות מוצגים ללא מע"מ
        {summary?.vatRate ? ` (${summary.vatRate}%)` : ''} — המע"מ נגבה עבור רשות המסים ואינו הכנסה של החברה.
        הנחות, תשלומים מיוחדים והוצאות נרשמים נטו ומוצגים כפי שהוזנו.
      </SectionNote>
      <SummaryGrid $cols={4}>
        <StatCard>
          <StatHeader>
            <StatTitle>סך הכנסות (תשלומי לקוחות, ללא מע"מ)</StatTitle>
            <StatIcon><TrendingUp size={18} strokeWidth={2} /></StatIcon>
          </StatHeader>
          <StatValue>{formatCurrency(summary?.clientPayments ?? 0)}</StatValue>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatTitle>סך תשלומים מיוחדים</StatTitle>
            <StatIcon><Coins size={18} strokeWidth={2} /></StatIcon>
          </StatHeader>
          <StatValue>{formatCurrency(summary?.specialPayments ?? 0)}</StatValue>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatTitle>סך הוצאות</StatTitle>
            <StatIcon><TrendingDown size={18} strokeWidth={2} /></StatIcon>
          </StatHeader>
          <StatValue>{formatCurrency(summary?.expenses ?? 0)}</StatValue>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatTitle>סך הנחות (ללא מע"מ)</StatTitle>
            <StatIcon><Tag size={18} strokeWidth={2} /></StatIcon>
          </StatHeader>
          <StatValue>{formatCurrency(summary?.discounts ?? 0)}</StatValue>
        </StatCard>
      </SummaryGrid>

      {/* שורה שנייה - מצב מעשר */}
      <SectionTitle>מצב מעשר</SectionTitle>
      <SummaryGrid>
        <StatCard $emphasis>
          <StatHeader>
            <StatTitle>מעשר לתשלום (10%)</StatTitle>
            <StatIcon><HandCoins size={18} strokeWidth={2} /></StatIcon>
          </StatHeader>
          <StatValue $primary>{formatCurrency(summary?.titheToPay ?? 0)}</StatValue>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatTitle>סכום ששולם</StatTitle>
            <StatIcon><CheckCircle size={18} strokeWidth={2} /></StatIcon>
          </StatHeader>
          <StatValue>{formatCurrency(summary?.amountPaid ?? 0)}</StatValue>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatTitle>יתרה לתשלום</StatTitle>
            <StatIcon><CircleDollarSign size={18} strokeWidth={2} /></StatIcon>
          </StatHeader>
          <StatValue $danger={(summary?.amountRemaining ?? 0) > 0}>
            {formatCurrency(summary?.amountRemaining ?? 0)}
          </StatValue>
        </StatCard>
      </SummaryGrid>

      {/* טבלת תשלומי המעשר (מסוננת לפי אותו טווח) */}
      <SectionTitle>תשלומי מעשר</SectionTitle>
      <DataTable<TithePaymentDto>
        data={payments}
        columns={columns}
        loading={loading}
        actions={
          <AddButton onClick={handleAdd}>
            <Plus size={18} />
            הוספת תשלום מעשר
          </AddButton>
        }
      />

      <TithePaymentFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSaved={refreshAll}
        payment={editingPayment}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="מחיקת תשלום מעשר"
        message={
          deleteTarget
            ? `האם למחוק את תשלום המעשר בסך ${formatCurrency(deleteTarget.amount)} מתאריך ${formatDate(deleteTarget.paymentDate)}?`
            : ''
        }
        confirmText="מחק"
        cancelText="ביטול"
        type="danger"
        loading={deleting}
      />
    </Container>
  );
};

export default TithesTab;
